import type {
  ChatResponseStatus,
  ChatSourceBundle,
} from "@/components/ai-chat/types";

import type { AgentExecution } from "@/types/agent-execution";
import type { AnalyticsChartType } from "@/types/analytics-ai";
import type { AIFeedbackType } from "@/types/enums";
import type { FileAttachment } from "@/types/file-attachment";

export enum AskDecodaRetryStatus {
  RETRYING = "RETRYING",
  RECOVERED = "RECOVERED",
}

export enum AttachmentKind {
  SPREADSHEET = "SPREADSHEET",
  DOCUMENT = "DOCUMENT",
  IMAGE = "IMAGE",
  UNKNOWN = "UNKNOWN",
}

export enum AttachmentSource {
  UPLOADED_FILE = "UPLOADED_FILE",
}

export enum AttachmentExtractionStatus {
  COMPLETE = "COMPLETE",
  REJECTED = "REJECTED",
}

export enum AskDecodaIndicatorStatus {
  NONE = "NONE",
  STREAMING = "STREAMING",
  COMPLETED = "COMPLETED",
  READ = "READ",
  BLOCKED = "BLOCKED",
  FAILED = "FAILED",
}

export interface ChatThreadSummary {
  threadId: string;
  /** Older SQL-backed Ask Decoda chats can be read or migrated, not resumed. */
  isLegacy?: boolean;
  firstQuestion: string;
  /** Assistant that owns the thread, e.g. PLATFORM_AGENT or TEMPLATE_ASSISTANT. */
  chatType?: string;
  /** Record a page assistant thread was editing. */
  entityId?: string | null;
  messageCount: number;
  createdDate: string;
  /** Latest visible operation timestamp; separate from the first display date. */
  latestActivity: string;
  latestStatus?: ChatResponseStatus;
  readAt?: string | null;
}

export interface AskDecodaMessageMetadata {
  feedbackType: AIFeedbackType | null;
  feedbackText: string | null;
  pendingFeedback?: boolean;
}

export type AskDecodaProductMetadata = Record<string, AskDecodaMessageMetadata>;

export interface ChatHistoryArtifactChartConfig extends Record<
  string,
  unknown
> {
  chart_type?: AnalyticsChartType | null;
  chart_title?: string | null;
  x_label?: string | null;
  y_label?: string | null;
  x_column?: string | null;
  y_columns?: string[] | null;
  currency_columns?: string[] | null;
}

export interface ChatHistoryArtifact {
  columns: string[] | null;
  rows: unknown[][] | null;
  rowCount: number | null;
  chartType: AnalyticsChartType | null;
  chartConfig: ChatHistoryArtifactChartConfig | null;
  sql: string | null;
  pythonCode: string | null;
  executionTimeMs: number | null;
  explanation: string | null;
  currencyColumnIndices: number[] | null;
  executionSteps: unknown[] | null;
}

export interface AskDecodaAttachmentFileContext {
  nativeFilePaths?: string[];
  name: string;
  size: number;
  type: string;
  kind: AttachmentKind;
  source: AttachmentSource;
  extractionStatus?: AttachmentExtractionStatus | null;
  extractedText?: string | null;
  truncated?: boolean;
  omittedChars?: number;
}

export interface AskDecodaAttachmentContext {
  version: 1;
  files: AskDecodaAttachmentFileContext[];
}

export enum AskDecodaOperationStatus {
  ACCEPTED = "ACCEPTED",
  RUNNING = "RUNNING",
  CANCEL_REQUESTED = "CANCEL_REQUESTED",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
}

/** Ordered, response-owned presentation derived from native checkpoints. */
export type AskDecodaResponseBlock =
  | { type: "text"; id: string; text: string }
  | { type: "execution"; id: string; executionId: string };

export interface AskDecodaSnapshotIdentity {
  operationChatId: string;
  responseChatId: string;
  /** A committed identity, not a sortable version or replay cursor. */
  checkpointId: string | null;
  operationStatus: AskDecodaOperationStatus;
}

/** Ask-owned payload discriminators, distinct from native SSE event names. */
export enum AskDecodaCustomEventType {
  METADATA = "METADATA",
  HISTORY_SNAPSHOT = "HISTORY_SNAPSHOT",
  TEXT_BLOCK_START = "TEXT_BLOCK_START",
  TOOL_PROGRESS = "TOOL_PROGRESS",
  MODEL_RETRY = "MODEL_RETRY",
  /** Retired event marker, deliberately not accepted by AskDecodaStreamEvent. */
  EXECUTION_SNAPSHOT = "EXECUTION_SNAPSHOT",
  /** Internal agent events projected to TOOL_PROGRESS by the Ask endpoint. */
  ANALYTICS_STEP = "ANALYTICS_STEP",
  SETTINGS_ACTIVITY = "SETTINGS_ACTIVITY",
  ANALYTICS_RESULT = "ANALYTICS_RESULT",
  SETTINGS_SOURCES = "SETTINGS_SOURCES",
  ERROR = "ERROR",
}

/** Custom-event payloads emitted by the embedded Ask Decoda endpoint. */
export type AskDecodaStreamEvent =
  | (AskDecodaSnapshotIdentity & {
      type: AskDecodaCustomEventType.METADATA;
      chatHistory: ChatHistoryEntry;
      requestId: string;
    })
  | (AskDecodaSnapshotIdentity & {
      type: AskDecodaCustomEventType.HISTORY_SNAPSHOT;
      entry: ChatHistoryEntry;
      requestId: string;
    })
  | {
      type: AskDecodaCustomEventType.TEXT_BLOCK_START;
      operationChatId: string;
      responseChatId: string;
      blockId: string;
      afterBlockId: string | null;
    }
  | {
      type: AskDecodaCustomEventType.TOOL_PROGRESS;
      message: string;
      operationChatId: string;
      responseChatId: string;
    }
  | {
      type: AskDecodaCustomEventType.MODEL_RETRY;
      status: AskDecodaRetryStatus;
      message?: string;
      operationChatId: string;
      responseChatId: string;
    };

export enum AskDecodaTodoStatus {
  PENDING = "PENDING",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
}

/** Planning statuses translated by the Ask Decoda todo middleware. */
export interface AskDecodaTodo {
  content: string;
  status: AskDecodaTodoStatus;
}

/** Native HITL decisions consumed directly by the LangGraph SDK. */
export type AskDecodaDecision = {
  type: "approve" | "reject";
  message?: string;
  selectedUnitIds?: string[];
};

export interface AskDecodaInterrupt {
  id: string;
  value: {
    action_requests: Array<{
      name: string;
      args: Record<string, unknown>;
      description?: string;
    }>;
    review_configs: Array<{
      action_name: string;
      allowed_decisions: Array<"approve" | "edit" | "reject">;
    }>;
  };
}

export interface AskDecodaApprovalOwner {
  threadId: string;
  responseChatId: string;
  interrupt: AskDecodaInterrupt;
  checkpointId: string;
  runtimeEpoch: number;
}

/** Presentation only; the native checkpoint is the authority for approvals. */
export interface AskDecodaRuntimeState {
  todos: AskDecodaTodo[];
  todoTitle?: string | null;
  interrupts: AskDecodaInterrupt[];
  checkpointId?: string | null;
}

export interface ChatHistoryEntry {
  id: string;
  isLegacy?: boolean;
  runtimeState?: AskDecodaRuntimeState | null;
  /** Latest journal operation, distinct from the displayed response when resumed. */
  operationChatId?: string | null;
  /** Frontend-only identity used while an SSE response is still local/in-flight. */
  requestId?: string;
  /** Frontend-only marker set once the server persists the user question. */
  requestAccepted?: boolean;
  threadId: string;
  question: string;
  answer: string;
  /** Native Ask responses always carry blocks; other assistants may omit them. */
  responseBlocks?: AskDecodaResponseBlock[] | null;
  preamble?: string | null;
  status: ChatResponseStatus;
  userId: string;
  feedbackType: AIFeedbackType | null;
  feedbackText: string | null;
  modelName: string;
  createdDate: string;
  artifacts: ChatHistoryArtifact[];
  executions: AgentExecution[];
  sources?: ChatSourceBundle | null;
  /** Frontend-only optimistic attachments for the user question. */
  files?: FileAttachment[];
  /** Persisted, non-binary context used to ground later turns. */
  attachmentContext?: AskDecodaAttachmentContext | null;
  /** Frontend-only feedback in-flight marker. */
  pendingFeedback?: boolean;
  /** Frontend-only transient status text; never a persisted execution/card. */
  progressMessage?: string;
}
